import {
  PdfGen,
  PdfSize,
  PdfRect,
  PdfBrush,
  PdfColor,
  PageGen,
  PdfFont,
  PdfUnit,
  ETextAlign,
  PdfImage,
} from '../pdf';
import { GUtil } from '../gutil';
import { urlQr } from '../../core/urlQr';
import { OptsBase } from '../../layoutBase/optsBase';
import { ProviderBuilder, EGenMode } from '@educraft/base';

export enum EPageSize {
  A4 = 'A4',
  A4L = 'A4L',
  A5 = 'A5',
  A5L = 'A5L',
  A6 = 'A6',
  A6L = 'A6L',
  A7 = 'A7',
  A7L = 'A7L',
  A4_3 = 'A4_3',
  A4_4 = 'A4_4',
  A4_5 = 'A4_5',
}

export interface QrCont {
  pos: string;
  type: string;
  value: string;
  color: string;
  fontSize: number;
  data: any;
}

export interface QrCard {
  content: QrCont[];
}

export interface QrCommon {
  pos: string;
  content: QrCont[];
}

export interface QrProj {
  pageSize: string;
  cards: QrCard[];
  common: QrCommon;
}

export interface AnyQrImage {
  file: string;
  data: Uint8Array;
  type: 'png' | 'jpeg';
}

export interface OptsQr extends OptsBase {
  proj: QrProj;
  images: AnyQrImage[];
}

export const IAnyQrSymbol = Symbol.for('IAnyQr');

export interface IAnyQrGen {
  generate(pdf: PdfGen, opts: OptsQr): Promise<void>;
}

export class AnyQr implements IAnyQrGen {
  public async generate(pdf: PdfGen, opts: OptsQr): Promise<void> {
    const pageSize = opts.proj.pageSize as EPageSize;
    const count = opts.proj.cards.length;

    const pages = await this.getPages(pdf, count, pageSize);

    for (let i = 0; i < count; i++) {
      const { pg, rect } = pages[i];

      if (opts.proj && opts.proj.common) {
        if (opts.proj.common.content) {
          for (const content of opts.proj.common.content) {
            await this.drawContent(pg, opts, rect, content, opts.proj.common, i + 1, count);
          }
        }
      }

      if (opts.proj && opts.proj.cards[i]) {
        for (const content of opts.proj.cards[i].content) {
          await this.drawContent(pg, opts, rect, content, opts.proj.common, i + 1, count);
        }
      }

      pg.drawRectangle(GUtil.cutPen, null, rect);
    }
  }

  private async drawContent(
    pg: PageGen,
    opts: OptsQr,
    rect: PdfRect,
    content: QrCont,
    common: QrCommon,
    index: number,
    total: number
  ): Promise<void> {
    const r = this.divideRect(rect, common.pos, content.pos);
    const t = content.type.toUpperCase();
    const fontSize = content.fontSize > 0 ? content.fontSize : GUtil.baseFont.size;
    const font = new PdfFont(PdfFont.FONT_NAME, fontSize);
    let brush = PdfBrush.Black;

    if (content.color) {
      const color = this.colorFromName(content.color);
      brush = new PdfBrush(color);
    }

    if (t === 'TEXT') {
      pg.drawString(content.value, font, brush, r, ETextAlign.Center);
    } else if (t === 'INDEX') {
      pg.drawString(`#${index}/${total}`, font, brush, r, ETextAlign.Center);
    } else if (t === 'IMG') {
      const file = content.value;
      const imgData = opts.images.find((i) => i.file === file);
      if (imgData) {
        const pdfImage = await pg.pdf.embedImage(imgData.file, imgData.data, imgData.type);
        // PdfImage constructor in pdf.ts
        const wrappedImage = new PdfImage(
          pdfImage,
          pdfImage.width,
          pdfImage.height
        );
        pg.drawImageCenter(wrappedImage, r);
      }
    } else if (t === 'QR') {
      const url = urlQr(content.data);
      const rr = r.centerSquare();
      pg.drawQr(rr, brush, url);
    } else if (t === 'FILL') {
      pg.drawRectangle(null, brush, r);
    } else {
      throw new Error(`Not implemented content type: ${t}`);
    }
  }

  private colorFromName(name: string): PdfColor {
    if (name.startsWith('#')) return PdfColor.fromHex(name);
    // Basic color map
    const colors: Record<string, string> = {
      Red: '#FF0000',
      Green: '#008000',
      Blue: '#0000FF',
      Yellow: '#FFFF00',
      Black: '#000000',
      White: '#FFFFFF',
      Gray: '#808080',
      Pink: '#FFC0CB',
      Orange: '#FFA500',
      Purple: '#800080',
      Brown: '#A52A2A',
      Cyan: '#00FFFF',
      Magenta: '#FF00FF',
    };
    const hex = colors[name] || colors[name.charAt(0).toUpperCase() + name.slice(1)];
    if (hex) return PdfColor.fromHex(hex);
    return PdfColor.fromRgb(255, 192, 203); // Pink as fallback
  }

  private divideRect(rect: PdfRect, ...commands: string[]): PdfRect {
    const array = commands
      .filter((i) => !!i)
      .flatMap((i) => i.split('-'));

    let currentRect = rect;

    const regex = /((m)(\d+)?|((r|c))(\d+)\.(\d+))(\.(\d))?/;
    for (const str of array) {
      // Use exec instead of match to satisfy lint rule @typescript-eslint/prefer-regexp-exec
      const match = regex.exec(str);
      if (!match) continue;

      const cmd = match[2] || match[5];

      if (cmd === 'm') {
        let margin = PdfUnit.FromMillimeters(5);
        if (match[3]) {
          margin = PdfUnit.FromMillimeters(parseInt(match[3]));
        }
        currentRect = currentRect.deflate(margin);
      } else if (cmd === 'r') {
        const count = parseInt(match[6]);
        const number = parseInt(match[7]);
        const span = match[9] ? parseInt(match[9]) : 1;

        const h = currentRect.height / count;
        currentRect = new PdfRect(
          currentRect.x,
          currentRect.y + (number - 1) * h,
          currentRect.width,
          h * span
        );
      } else if (cmd === 'c') {
        const count = parseInt(match[6]);
        const number = parseInt(match[7]);
        const span = match[9] ? parseInt(match[9]) : 1;

        const w = currentRect.width / count;
        currentRect = new PdfRect(
          currentRect.x + (number - 1) * w,
          currentRect.y,
          w * span,
          currentRect.height
        );
      }
    }

    return currentRect;
  }

  private async getPages(
    pdf: PdfGen,
    count: number,
    pageSize: EPageSize
  ): Promise<{ pg: PageGen; rect: PdfRect }[]> {
    const list: { pg: PageGen; rect: PdfRect }[] = [];

    let itemsPerPage = 1;
    let countX = 1;
    let countY = 1;
    let landscape = false;

    switch (pageSize) {
      case EPageSize.A4:
        itemsPerPage = 1;
        countX = 1;
        countY = 1;
        landscape = false;
        break;
      case EPageSize.A4L:
        itemsPerPage = 1;
        countX = 1;
        countY = 1;
        landscape = true;
        break;
      case EPageSize.A5:
        itemsPerPage = 2;
        countX = 2;
        countY = 1;
        landscape = true;
        break;
      case EPageSize.A5L:
        itemsPerPage = 2;
        countX = 1;
        countY = 2;
        landscape = false;
        break;
      case EPageSize.A6:
        itemsPerPage = 4;
        countX = 2;
        countY = 2;
        landscape = false;
        break;
      case EPageSize.A6L:
        itemsPerPage = 4;
        countX = 2;
        countY = 2;
        landscape = true;
        break;
      case EPageSize.A7:
        itemsPerPage = 8;
        countX = 4;
        countY = 2;
        landscape = true;
        break;
      case EPageSize.A7L:
        itemsPerPage = 8;
        countX = 2;
        countY = 4;
        landscape = false;
        break;
      case EPageSize.A4_3:
        itemsPerPage = 3;
        countX = 1;
        countY = 3;
        landscape = false;
        break;
      case EPageSize.A4_4:
        itemsPerPage = 4;
        countX = 1;
        countY = 4;
        landscape = false;
        break;
      case EPageSize.A4_5:
        itemsPerPage = 5;
        countX = 1;
        countY = 5;
        landscape = false;
        break;
      default:
        throw new Error(`Not implemented page size: ${pageSize}`);
    }

    const pageCount = Math.ceil(count / itemsPerPage);
    const startPageIdx = pdf.gs.length;

    for (let i = 0; i < pageCount; i++) {
      pdf.addPage(landscape);
    }

    const basePageSize = landscape ? PdfSize.A4H : PdfSize.A4;
    const size = new PdfSize(basePageSize.width / countX, basePageSize.height / countY);

    for (let p = 0; p < pageCount; p++) {
      const pg = pdf.gs[startPageIdx + p];
      for (let y = 0; y < countY; y++) {
        for (let x = 0; x < countX; x++) {
          const index = p * countX * countY + y * countX + x;
          if (index < count) {
            list.push({
              pg,
              rect: new PdfRect(size.width * x, size.height * y, size.width, size.height),
            });
          }
        }
      }
    }

    return list;
  }
}

export function registerAnyQr(builder: ProviderBuilder): void {
  builder.add(EGenMode.AnyQr, AnyQr, 'AnyQr');
}
